import React from 'react';
import styled from 'styled-components';

import Timer from '../../../Timer';
import Name from '../../Clients/Name';
import Button from '../../../Button';

import {
  Call,
  Listen,
  Help,
  OnAir,
  Transfer,
} from '../../../../icons-react/dialer';
import { ValueOf } from '@utils/tsUtils';

import { VOX_STATES } from '@constants/vox';

const LOCK_TIMEOUT = 4000;

type CallType = {
  id?: string;
  pbxId?: string;
  duration?: number;
};

type ContactType = {
  name?: string;
  number?: number;
  isHidden?: boolean;
};

type OperatorType = {
  name: string;
  number: string;
  state: string;
  skillGroups: string[];
  canCall: boolean;
  canTransfer: boolean;
  canListen: boolean;
  canRequestHelp: boolean;
  isCallCenter?: boolean;
};

export interface OperatorProps {
  className?: string;
  call?: CallType;
  contact?: ContactType;
  operator: OperatorType;
  onCall: ({ value }: { value: OperatorType['number'] }) => void;
  onTransfer: ({ operatorId }: { operatorId: OperatorType['number'] }) => void;
  onListen: ({ pbxId }: { pbxId: string }) => void;
  onRequestHelp: ({
    supervisorID,
  }: {
    supervisorID: OperatorType['number'];
  }) => void;
}

interface OperatorState {
  locked: boolean;
}

class Operator extends React.PureComponent<OperatorProps, OperatorState> {
  static defaultProps = {
    className: '',
    call: null,
    contact: null,
  };

  static status({ number, state }) {
    const isServicing = state === VOX_STATES.IN_SERVICE;
    return (
      <StyledStatus status={state}>
        {isServicing && <StyledOnAirIcon />}
        {number}
        {isServicing && <StyledOnAirIcon />}
      </StyledStatus>
    );
  }

  static duration({ duration }: { duration?: CallType['duration'] }) {
    return (
      duration && (
        <StyledDuration
          key={duration}
          initialSeconds={duration}
          format="m:ss"
        />
      )
    );
  }

  constructor(props) {
    super(props);

    this.state = { locked: false };

    this.handleCall = this.handleCall.bind(this);
    this.handleListen = this.handleListen.bind(this);
    this.handleTransfer = this.handleTransfer.bind(this);
    this.handleRequestHelp = this.handleRequestHelp.bind(this);
    this.toggleOperatorLock = this.toggleOperatorLock.bind(this);
  }

  handleRequestHelp() {
    const { operator, onRequestHelp } = this.props;
    onRequestHelp({ supervisorID: operator.number });
    this.toggleOperatorLock(true);
  }

  handleTransfer() {
    const { operator, onTransfer } = this.props;
    onTransfer({ operatorId: operator.number });
    this.toggleOperatorLock(true);
  }

  handleListen() {
    const { call, onListen } = this.props;
    if (!call || !call.pbxId) return;
    const { pbxId } = call;
    onListen({ pbxId });
    this.toggleOperatorLock(true);
  }

  handleCall() {
    const { operator, onCall } = this.props;
    onCall({ value: operator.number });
    this.toggleOperatorLock(true);
  }

  toggleOperatorLock(locked = false) {
    this.setState({ locked });

    if (locked) {
      setTimeout(this.toggleOperatorLock, LOCK_TIMEOUT);
    }
  }

  about() {
    const { contact, operator } = this.props;
    const { name: operatorName, skillGroups } = operator;

    return (
      <StyledAbout>
        <StyledAboutTitle>
          <StyledOperatorName name={operatorName} />
          {skillGroups && <StyledGroups>{skillGroups.join()}</StyledGroups>}
          {this.helpers()}
        </StyledAboutTitle>
        {!!contact && (
          <StyledClient>
            {contact.isHidden ? (
              'Номер скрыт'
            ) : (
              <StyledNumber>
                {contact.name}
                {contact.number && <span>{` (${contact.number})`}</span>}
              </StyledNumber>
            )}
          </StyledClient>
        )}
      </StyledAbout>
    );
  }

  actions() {
    const { operator } = this.props;

    const { locked } = this.state;

    const { canCall, canTransfer } = operator;

    return (
      <StyledActions>
        {canCall && (
          <StyledActionButton disabled={locked} onClick={this.handleCall}>
            <StyledCallIcon />
          </StyledActionButton>
        )}
        {canTransfer && (
          <StyledActionButton disabled={locked} onClick={this.handleTransfer}>
            <StyledTransferIcon />
          </StyledActionButton>
        )}
      </StyledActions>
    );
  }

  helpers() {
    const { call, operator } = this.props;

    const hasPbxId = call && call.pbxId;
    const { locked } = this.state;

    const { canListen, canRequestHelp } = operator;

    return (
      <StyledHelpers>
        {hasPbxId && canListen && (
          <StyledActionButton disabled={locked} onClick={this.handleListen}>
            <StyledListenIcon />
          </StyledActionButton>
        )}
        {canRequestHelp && (
          <StyledActionButton
            disabled={locked}
            onClick={this.handleRequestHelp}
          >
            <StyledHelpIcon />
          </StyledActionButton>
        )}
      </StyledHelpers>
    );
  }

  render() {
    const { operator, call, ...props } = this.props;
    return (
      <StyledOperator {...props}>
        {Operator.status(operator)}
        {this.actions()}
        {this.about()}
        {call && Operator.duration(call)}
      </StyledOperator>
    );
  }
}

const StyledOperator = styled.li`
  width: 100%;
  display: flex;
  margin: 10px 0;
  align-items: center;
`;

const StyledStatus = styled.div<{ status: ValueOf<typeof VOX_STATES> }>`
  width: 38px;
  color: #17181a;
  margin: 0 10px;
  font-size: 14px;
  position: relative;
  border-radius: 2px;
  text-align: center;
  padding: 2px 0 3px;

  ${p =>
    p.status === VOX_STATES.DND &&
    `
    color: #fff;
    background-image: linear-gradient(to right, #e5200e, #ff860c);
  `}

  ${p =>
    p.status === VOX_STATES.ONLINE &&
    `
    background-image: linear-gradient(to right, #f7aa1b, #faed61);
  `}
  
  ${p =>
    p.status === VOX_STATES.READY &&
    `
    color: #fff;
    background-image: linear-gradient(to right, #00b0b6, #00c87f);
  `}
  
  ${p =>
    ![
      VOX_STATES.DND,
      VOX_STATES.IN_SERVICE,
      VOX_STATES.ONLINE,
      VOX_STATES.READY,
    ].includes(p.status) &&
    `
    color: #919499;
    border: 1px solid #919499;
  `}
`;

const StyledOnAirIcon = styled(OnAir)`
  top: 0;
  height: 17px;
  right: -3px;
  position: absolute;

  &:first-child {
    left: -3px;
    transform: rotate(180deg);
  }
`;

const StyledActions = styled.div`
  width: 22px;
  margin-right: 7px;
`;

const StyledHelpers = styled.div`
  position: relative;
`;

const StyledActionButton = styled(Button)`
  cursor: pointer;
  padding: 0;
  border: 0;
  width: auto;
  height: auto;
  overflow: visible;
  background: none;
  line-height: normal;

  ${p =>
    p.disabled &&
    `
    opacity: .9;
    pointer-events: none;
  `}
`;

const StyledListenIcon = styled(Listen)`
  width: 15px;
  height: auto;
  cursor: pointer;
  margin-top: -6px;
  margin-left: 7px;
  vertical-align: middle;
`;

const StyledHelpIcon = styled(Help)`
  width: 23px;
  height: auto;
  cursor: pointer;
  margin-top: -5px;
  margin-left: 7px;
  vertical-align: middle;
`;

const StyledAbout = styled.div`
  flex: 1;
  min-width: 250px;
  padding-right: 40px;
  box-sizing: border-box;
`;

const StyledAboutTitle = styled.div`
  display: flex;
`;

const StyledOperatorName = styled(Name)`
  color: #1b63ac;
  font-size: 14px;
  font-weight: normal;
  min-width: fit-content;
`;

const StyledGroups = styled.span`
  color: #919499;
  overflow: hidden;
  text-align: left;
  margin-left: 10px;
  margin-right: 2px;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyledClient = styled.div`
  color: #17181a;
  font-size: 12px;
  text-align: left;
`;

const StyledNumber = styled.div`
  font-size: 12px;
`;

const StyledDuration = styled(Timer)`
  width: 45px;
  font-size: 14px;
  text-align: right;
  font-weight: normal;
`;

const StyledTransferIcon = styled(Transfer)`
  cursor: pointer;
  height: auto;
  vertical-align: middle;
  width: 22px;

  path {
    stroke: #1b63ac;
  }
`;

const StyledCallIcon = styled(Call)`
  cursor: pointer;
  height: auto;
  vertical-align: middle;
  width: 18px;

  path {
    stroke: #1b63ac;
  }
`;

export default Operator;
